import { useCallback, useEffect, useState } from 'react'
import WebApp from '@twa-dev/sdk'

type HomeScreenStatus = 'unsupported' | 'unknown' | 'added' | 'missed'

interface UseHomeScreenInstall {
  status: HomeScreenStatus
  showHeaderInstallButton: boolean
  addToHomeScreen: () => void
}

const logInstallDebug = (message: string, details?: Record<string, unknown>) => {
  if (!import.meta.env.DEV) return
  if (details) {
    console.info(`[TMA][A2HS] ${message}`, details)
    return
  }
  console.info(`[TMA][A2HS] ${message}`)
}

const isTelegramContext = (): boolean => {
  if (typeof window === 'undefined') return false
  const runtime = (window as Window & { Telegram?: { WebApp?: unknown } }).Telegram?.WebApp
  return Boolean(runtime)
}

const isMobilePlatform = (): boolean => {
  try {
    const p = WebApp.platform
    return p === 'android' || p === 'android_x' || p === 'ios'
  } catch {
    return false
  }
}

export const useHomeScreenInstall = (): UseHomeScreenInstall => {
  const [status, setStatus] = useState<HomeScreenStatus>('unknown')
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const inTelegram = isTelegramContext()
    if (!inTelegram) {
      setStatus('unsupported')
      logInstallDebug('Not in Telegram context, hide install button')
      return
    }

    const mobile = isMobilePlatform()
    setIsMobile(mobile)
    logInstallDebug('Telegram context detected', {
      platform: WebApp.platform,
      version: WebApp.version,
      isMobilePlatform: mobile,
    })

    if (typeof WebApp.checkHomeScreenStatus !== 'function') {
      setStatus('unsupported')
      logInstallDebug('checkHomeScreenStatus is unavailable')
      return
    }

    try {
      WebApp.checkHomeScreenStatus((s) => {
        setStatus(s)
        logInstallDebug('Initial home screen status', { status: s })
      })
    } catch (error) {
      setStatus('unsupported')
      logInstallDebug('checkHomeScreenStatus threw', {
        error: error instanceof Error ? error.message : String(error),
      })
      return
    }

    const onChecked = (params: { status: HomeScreenStatus }) => {
      setStatus(params.status)
      logInstallDebug('homeScreenChecked event', { status: params.status })
    }

    const onAdded = () => {
      setStatus('added')
      logInstallDebug('homeScreenAdded event')
    }

    try {
      WebApp.onEvent('homeScreenChecked', onChecked)
      WebApp.onEvent('homeScreenAdded', onAdded)
    } catch (error) {
      logInstallDebug('onEvent subscription failed', {
        error: error instanceof Error ? error.message : String(error),
      })
    }

    return () => {
      try {
        WebApp.offEvent('homeScreenChecked', onChecked)
        WebApp.offEvent('homeScreenAdded', onAdded)
      } catch {
        // ignore cleanup errors
      }
    }
  }, [])

  const addToHomeScreen = useCallback(() => {
    try {
      logInstallDebug('Calling addToHomeScreen', {
        platform: WebApp.platform,
        status,
      })
      WebApp.addToHomeScreen()
    } catch (error) {
      logInstallDebug('addToHomeScreen threw', {
        error: error instanceof Error ? error.message : String(error),
      })
    }
  }, [status])

  const showHeaderInstallButton = isMobile && (status === 'unknown' || status === 'missed')

  return { status, showHeaderInstallButton, addToHomeScreen }
}
